import {
    COMPOSITION_DATA_KEY,
    DEFAULT_LAYOUT_CONTEXT,
    FieldType,
    FRAGMENT_PATH_PREFIX,
    GRID_BASE_KEYS,
    GridKey,
    HOMEPAGE_PATH,
    RenderMode,
    StagedOperation,
    TABLE_ROW_ID_KEY,
    TABLE_ROW_ID_PATTERN,
    altKey,
    gridFieldKey,
    isFileFieldType,
    isTruthyFlag,
    positionKey,
} from '../vocabulary.js';

function normalizeLayoutData(data, layoutConfig) {
    const normalized = {...data};
    const contexts = layoutConfig?.contexts || null;
    if (!contexts || Object.keys(contexts).length === 0) {
        return normalized;
    }
    for (const [context, config] of Object.entries(contexts)) {
        const enabledByField = config?.enabledByField;
        // Use strict truthy check: only `true` (boolean) or `'true'` (FormData string) enable layout.
        // `Boolean("false")` would be truthy, so avoid using Boolean() on the raw value.
        const value = normalized[enabledByField];
        const participatesInLayout = !enabledByField || value === true || String(value ?? '').trim() === 'true';
        if (participatesInLayout) {
            continue;
        }
        for (const key of gridFieldKeysForContext(context)) {
            delete normalized[key];
        }
    }
    return normalized;
}

export function gridFieldKeysForContext(context) {
    return GRID_BASE_KEYS.map((base) => gridFieldKey(context, base));
}

/** Matches a context-prefixed grid field key, capturing the context and the base key. */
const CONTEXT_GRID_KEY_PATTERN = new RegExp(`^([a-zA-Z0-9_]+)_(${GRID_BASE_KEYS.join('|')})$`);

export function inferLayoutContexts(fields, layoutConfig = null) {
    const configuredContexts = Object.keys(layoutConfig?.contexts || {});
    const inferredContexts = [];
    const fieldKeys = new Set((fields || []).map((field) => field.key));

    if (fieldKeys.has(GridKey.ROW) && fieldKeys.has(GridKey.COLUMN)) {
        inferredContexts.push(DEFAULT_LAYOUT_CONTEXT);
    }

    const contextualGridFields = new Map();
    for (const key of fieldKeys) {
        const match = key.match(CONTEXT_GRID_KEY_PATTERN);
        if (!match) {
            continue;
        }
        const [, context, baseKey] = match;
        if (!contextualGridFields.has(context)) {
            contextualGridFields.set(context, new Set());
        }
        contextualGridFields.get(context).add(baseKey);
    }
    for (const [context, keys] of contextualGridFields.entries()) {
        if (keys.has(GridKey.ROW) && keys.has(GridKey.COLUMN)) {
            inferredContexts.push(context);
        }
    }

    const orderedContexts = [];
    if (configuredContexts.includes(DEFAULT_LAYOUT_CONTEXT) || inferredContexts.includes(DEFAULT_LAYOUT_CONTEXT)) {
        orderedContexts.push(DEFAULT_LAYOUT_CONTEXT);
    }
    for (const context of configuredContexts) {
        if (context !== DEFAULT_LAYOUT_CONTEXT && !orderedContexts.includes(context)) {
            orderedContexts.push(context);
        }
    }
    for (const context of inferredContexts) {
        if (context !== DEFAULT_LAYOUT_CONTEXT && !orderedContexts.includes(context)) {
            orderedContexts.push(context);
        }
    }

    return orderedContexts;
}

export function isLayoutEnabled(layoutConfig, context = DEFAULT_LAYOUT_CONTEXT, formElement, fields = []) {
    const enabledByField = layoutConfig?.contexts?.[context]?.enabledByField || '';
    if (!enabledByField) {
        const keys = new Set((fields || []).map((field) => field.key));
        return keys.has(gridFieldKey(context, GridKey.ROW)) && keys.has(gridFieldKey(context, GridKey.COLUMN));
    }
    const input = formElement?.querySelector?.(`[name="${enabledByField}"]`);
    if (!input) {
        return false;
    }
    if (input.type === 'checkbox') {
        return Boolean(input.checked);
    }
    return isTruthyFlag(input.value);
}

const ROW_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const ROW_ID_LENGTH = 5;
/** Decimal numbers as the backend parses them (`BigDecimal`): sign, digits, point, exponent. */
const TABLE_NUMBER_PATTERN = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/**
 * Whether a `number` cell value is acceptable: blank, or a decimal number.
 * @param {string} value
 * @returns {boolean}
 */
export function isTableNumber(value) {
    return value === '' || TABLE_NUMBER_PATTERN.test(value);
}

/**
 * Short random base36 id for a new table row, unique among `taken`.
 * @param {Iterable<string>} [taken]
 * @returns {string}
 */
export function newRowId(taken = []) {
    const used = new Set(taken);
    let id;
    do {
        id = '';
        for (let i = 0; i < ROW_ID_LENGTH; i++) {
            id += ROW_ID_ALPHABET[Math.floor(Math.random() * ROW_ID_ALPHABET.length)];
        }
    } while (used.has(id));
    return id;
}

/**
 * Shapes table rows the way the backend stores them: every row carries a unique `_id` and
 * every column key as a string (`""` when blank), and nothing else. Rows without a valid id
 * (letters, digits, `_` and `-`, at most 64 characters), or with an id already used by an
 * earlier row, get a fresh one.
 * @param {import('../../shell/types/user.types.js').TableColumn[]} columns
 * @param {unknown} rows
 * @returns {import('../../shell/types/user.types.js').TableRow[]}
 */
export function normalizeTableRows(columns, rows) {
    if (!Array.isArray(rows)) return [];
    const used = new Set();
    return rows
        .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
        .map((row) => {
            const savedId = row[TABLE_ROW_ID_KEY];
            const id = typeof savedId === 'string' && TABLE_ROW_ID_PATTERN.test(savedId) && !used.has(savedId) ? savedId : newRowId(used);
            used.add(id);
            const normalized = {[TABLE_ROW_ID_KEY]: id};
            for (const column of columns || []) {
                const value = row[column.key];
                normalized[column.key] = value === null || value === undefined ? '' : String(value);
            }
            return normalized;
        });
}

/**
 * Multipart part name for a new file of one list entry: a table row's document cell or a
 * repeatable section item's image or document field.
 * @param {string} listKey Table field key or repeatable `dataKey`
 * @param {string} itemId Row or item `_id`
 * @param {string} fieldKey Column or item field key
 * @returns {string} e.g. "units.r7Kq2.plan" or "notes.k3x9a.photo"
 */
export function listFilePartName(listKey, itemId, fieldKey) {
    return `${listKey}.${itemId}.${fieldKey}`;
}

/**
 * The repeatable item lists of the form, keyed by data key, each with its item editors in DOM
 * order (which is page order). A list key is announced by any element carrying
 * `data-list-key`: the section editor marks every repeatable section that way, so a list
 * without items is still found and sent as `[]`.
 * @param {ParentNode} formElement
 * @returns {Map<string, import('../../components/section-item-editor/section-item-editor.component.js').SectionItemEditor[]>}
 */
function readItemLists(formElement) {
    const lists = new Map();
    for (const element of formElement.querySelectorAll('[data-list-key]')) {
        lists.set(element.dataset.listKey, []);
    }
    for (const editor of formElement.querySelectorAll('section-item-editor[data-list-key]')) {
        lists.get(editor.dataset.listKey).push(editor);
    }
    return lists;
}

/**
 * An item for the JSON preview endpoint: a pending image becomes a data URL and a pending
 * document its file name, as for top-level fields.
 * @param {import('../../components/section-item-editor/section-item-editor.component.js').SectionItemEditor} editor
 * @returns {Promise<Object<string, string>>}
 */
async function previewItem(editor) {
    const item = editor.item;
    const types = new Map(editor.fields.map((field) => [field.key, field.type]));
    for (const {fieldKey, file} of editor.files) {
        item[fieldKey] = types.get(fieldKey) === FieldType.IMAGE ? await toBase64(file) : file.name;
    }
    return item;
}

/**
 * Rows for the JSON preview endpoints: a pending (not yet uploaded) document cell holds the
 * chosen file's name, as top-level documents do.
 * @param {import('../../shell/types/user.types.js').TableRow[]} rows
 * @param {import('../../shell/types/user.types.js').PendingTableFile[]} files
 * @returns {import('../../shell/types/user.types.js').TableRow[]}
 */
export function tablePreviewRows(rows, files) {
    const pending = new Map((files || []).map((entry) => [`${entry.rowId}\u0000${entry.columnKey}`, entry.file.name]));
    return (rows || []).map((row) => {
        const copy = {...row};
        for (const key of Object.keys(copy)) {
            const name = pending.get(`${row[TABLE_ROW_ID_KEY]}\u0000${key}`);
            if (name !== undefined) copy[key] = name;
        }
        return copy;
    });
}

/**
 * Current rows and pending uploads of a table field control in the form, or null when the
 * form has no control for the key.
 * @param {ParentNode} formElement
 * @param {import('../../shell/types/user.types.js').TemplateField} field
 * @returns {{rows: import('../../shell/types/user.types.js').TableRow[], files: import('../../shell/types/user.types.js').PendingTableFile[]} | null}
 */
function readTableField(formElement, field) {
    const control = formElement.querySelector(`[name="${field.key}"]`);
    if (!control) return null;
    return {
        rows: normalizeTableRows(field.columns, control.rows),
        files: control.files,
    };
}

/**
 * Builds a FormData object for the publish page multipart request.
 * Iterates over the template field schema and extracts values from the form DOM. Each
 * repeatable item list is sent as one JSON part (`{dataKey}`, items in page order) plus a file
 * part `{dataKey}.{itemId}.{fieldKey}` per pending item upload.
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {HTMLFormElement} formElement
 * @param {string} templateName
 * @param {{contexts?: Record<string, {enabledByField?: string}>} | null} [layoutConfig]
 * @returns {FormData}
 */
export function toPublishFormData(fields, formElement, templateName, layoutConfig = null) {
    const fd = new FormData();
    fd.append('templateName', templateName.trim());

    const fieldKeys = new Set(fields.map(f => f.key));
    const processedKeys = new Set();

    for (const field of fields) {
        if (processedKeys.has(field.key)) {
            continue;
        }
        processedKeys.add(field.key);

        if (field.type === FieldType.IMAGE) {
            const input = formElement.querySelector(`[name="${field.key}"]`);
            if (input && input.files && input.files[0]) {
                fd.append(field.key, input.files[0]);
            }

            const imageAltKey = altKey(field.key);
            if (!fieldKeys.has(imageAltKey)) {
                const altInput = formElement.querySelector(`[name="${imageAltKey}"]`);
                if (altInput) {
                    fd.append(imageAltKey, altInput.value.trim());
                }
            }

            const imagePositionKey = positionKey(field.key);
            if (!fieldKeys.has(imagePositionKey)) {
                const positionInput = formElement.querySelector(`[name="${imagePositionKey}"]`);
                if (positionInput) {
                    fd.append(imagePositionKey, positionInput.value);
                }
            }
        } else if (field.type === FieldType.DOCUMENT) {
            const input = formElement.querySelector(`[name="${field.key}"]`);
            if (input && input.files && input.files[0]) {
                fd.append(field.key, input.files[0]);
            }
        } else if (field.type === FieldType.TABLE) {
            const table = readTableField(formElement, field);
            if (table) {
                fd.append(field.key, JSON.stringify(table.rows));
                for (const {rowId, columnKey, file} of table.files) {
                    fd.append(listFilePartName(field.key, rowId, columnKey), file);
                }
            }
        } else {
            const input = formElement.querySelector(`[name="${field.key}"]`);
            if (input) {
                if (field.type === FieldType.CHECKBOX) {
                    // Unchecked is sent as empty to avoid truthy "false" strings in template conditions.
                    fd.append(field.key, input.checked ? 'true' : '');
                } else {
                    fd.append(field.key, String(input.value ?? '').trim());
                }
            }
        }
    }

    const normalizedData = normalizeLayoutData(Object.fromEntries(fd.entries()), layoutConfig);
    for (const key of [...fd.keys()]) {
        if (key === 'templateName') {
            continue;
        }
        if (!(key in normalizedData)) {
            fd.delete(key);
        }
    }
    for (const [listKey, editors] of readItemLists(formElement)) {
        fd.append(listKey, JSON.stringify(editors.map((editor) => editor.item)));
        for (const editor of editors) {
            for (const {fieldKey, file} of editor.files) {
                fd.append(listFilePartName(listKey, editor.item[TABLE_ROW_ID_KEY], fieldKey), file);
            }
        }
    }
    const composition = readCompositionInput(formElement);
    if (composition) {
        fd.append(COMPOSITION_DATA_KEY, composition);
    }
    return fd;
}

/**
 * Serialized section-composition state held by the form's hidden input, or null when the
 * template has no composition (or its saved state could not be restored).
 * @param {HTMLFormElement} formElement
 * @returns {string | null}
 */
function readCompositionInput(formElement) {
    const input = formElement.querySelector(`[name="${COMPOSITION_DATA_KEY}"]`);
    const value = input?.value?.trim();
    return value ? value : null;
}

/**
 * Builds a FormData object for the edit page multipart request.
 * Appends pagePath alongside all publish fields.
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {HTMLFormElement} formElement
 * @param {string} templateName
 * @param {string} pagePath
 * @param {{contexts?: Record<string, {enabledByField?: string}>} | null} [layoutConfig]
 * @returns {FormData}
 */
export function toEditFormData(fields, formElement, templateName, pagePath, layoutConfig = null) {
    const fd = toPublishFormData(fields, formElement, templateName, layoutConfig);
    fd.append('pagePath', pagePath);
    return fd;
}

/**
 * Builds a simple data object for the preview request.
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {HTMLFormElement} formElement
 * @param {{contexts?: Record<string, {enabledByField?: string}>} | null} [layoutConfig]
 * @returns {Promise<Object>}
 */
export async function toPreviewData(fields, formElement, layoutConfig = null) {
    const data = {};
    const processedKeys = new Set();
    for (const field of fields) {
        if (processedKeys.has(field.key)) {
            continue;
        }
        processedKeys.add(field.key);

        const input = formElement.querySelector(`[name="${field.key}"]`);
        if (!input) continue;

        if (field.type === FieldType.IMAGE) {
            if (input.files && input.files[0]) {
                data[field.key] = await toBase64(input.files[0]);
            } else if (input.dataset.existingPath) {
                // Extract just the filename from any existing path
                data[field.key] = input.dataset.existingPath.split('/').pop();
            }
            const altInput = formElement.querySelector(`[name="${altKey(field.key)}"]`);
            if (altInput) {
                data[altKey(field.key)] = altInput.value.trim();
            }
            const positionInput = formElement.querySelector(`[name="${positionKey(field.key)}"]`);
            if (positionInput) {
                data[positionKey(field.key)] = positionInput.value;
            }
        } else if (field.type === FieldType.TABLE) {
            const table = readTableField(formElement, field);
            data[field.key] = tablePreviewRows(table?.rows, table?.files);
        } else if (field.type === FieldType.DOCUMENT) {
            if (input.files && input.files[0]) {
                data[field.key] = input.files[0].name;
            } else if (input.dataset.existingPath) {
                data[field.key] = input.dataset.existingPath;
            } else {
                data[field.key] = '';
            }
        } else {
            if (field.type === FieldType.CHECKBOX) {
                data[field.key] = input.checked;
            } else {
                data[field.key] = input.value.trim();
            }
        }
    }
    for (const [listKey, editors] of readItemLists(formElement)) {
        data[listKey] = await Promise.all(editors.map(previewItem));
    }
    const composition = readCompositionInput(formElement);
    if (composition) {
        data[COMPOSITION_DATA_KEY] = JSON.parse(composition);
    }
    return normalizeLayoutData(data, layoutConfig);
}

/**
 * Builds a lightweight data object for layout preview only.
 * Skips binary file processing to keep layout checks responsive.
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {HTMLFormElement} formElement
 * @param {{contexts?: Record<string, {enabledByField?: string}>} | null} [layoutConfig]
 * @returns {Object}
 */
export function toLayoutPreviewData(fields, formElement, layoutConfig = null) {
    const data = {};
    const processedKeys = new Set();
    for (const field of fields) {
        if (processedKeys.has(field.key)) {
            continue;
        }
        processedKeys.add(field.key);

        const input = formElement.querySelector(`[name="${field.key}"]`);
        if (!input) continue;

        if (isFileFieldType(field.type)) {
            // Layout preview does not require image or document contents.
            continue;
        }

        if (field.type === FieldType.TABLE) {
            const table = readTableField(formElement, field);
            data[field.key] = tablePreviewRows(table?.rows, table?.files);
            continue;
        }

        if (field.type === FieldType.CHECKBOX) {
            data[field.key] = input.checked;
        } else {
            data[field.key] = input.value.trim();
        }
    }
    return normalizeLayoutData(data, layoutConfig);
}

/**
 * sessionStorage key holding an array of page paths that just successfully
 * published or were edited. The page-list pulses these rows on its next
 * render and clears the entry. sessionStorage (not memory) so the value
 * survives the hash navigation between form → list.
 */
const JUST_PUBLISHED_STORAGE_KEY = 'wb_just_published_paths';

/**
 * Records the page paths that just finished a successful publish/edit cycle so
 * the page-list can pulse them on its next render. Safe to call with an empty
 * iterable (no-op). Silently swallows storage errors — the pulse is cosmetic
 * and not worth surfacing as an error in private-browsing/quota scenarios.
 *
 * @param {Iterable<string>} paths
 */
export function recordJustPublishedPaths(paths) {
    const list = [...paths].filter((p) => typeof p === 'string' && p.length > 0);
    if (list.length === 0) return;
    try {
        sessionStorage.setItem(JUST_PUBLISHED_STORAGE_KEY, JSON.stringify(list));
    } catch {
        // intentionally swallowed — see jsdoc.
    }
}

/**
 * Reads the recorded paths and immediately clears the entry. Returns an empty
 * array on missing/corrupt data. The one-shot semantics ensure a pulse fires
 * exactly once per recorded batch.
 *
 * @returns {string[]}
 */
export function readAndClearJustPublishedPaths() {
    try {
        const raw = sessionStorage.getItem(JUST_PUBLISHED_STORAGE_KEY);
        if (!raw) return [];
        sessionStorage.removeItem(JUST_PUBLISHED_STORAGE_KEY);
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((p) => typeof p === 'string') : [];
    } catch {
        return [];
    }
}

/**
 * Exact mirror of the backend's `PathUtils.toSlug` for client-side URL previews
 * without a round-trip: transliterates accents (é to e), drops every other
 * character outside `a-z`, `0-9`, ASCII whitespace and hyphens, turns whitespace
 * runs into one hyphen and strips a single leading and trailing hyphen. Whitespace
 * is matched as `[ \t\n\v\f\r]` because Java's `\s` is ASCII-only, so a
 * non-breaking space is dropped on both sides. Default mode (`preserveHyphens=false`)
 * collapses repeated hyphens, useful when slugifying free-form titles. Set
 * `preserveHyphens=true` when the input is already a slug (matches the
 * backend's `toSlugPreservingHyphens`).
 *
 * @param {string} input
 * @param {boolean} [preserveHyphens=false]
 * @returns {string}
 */
export function slugify(input, preserveHyphens = false) {
    if (typeof input !== 'string') return '';
    if (input.trim().length === 0) return '';
    let slug = input.normalize('NFD')
        .replace(/\p{M}+/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9 \t\n\v\f\r-]/g, '')
        .replace(/[ \t\n\v\f\r]+/g, '-');
    if (!preserveHyphens) {
        slug = slug.replace(/-+/g, '-');
    }
    return slug.replace(/^-/, '').replace(/-$/, '');
}

/**
 * Java's `String.isBlank`: empty or only `Character.isWhitespace` characters, which include
 * the ASCII separators U+001C to U+001F but not the non-breaking spaces U+00A0, U+2007 and U+202F.
 */
const JAVA_BLANK = /^[\t\n\v\f\r\u001c-\u001f \u1680\u2000-\u2006\u2008-\u200a\u2028\u2029\u205f\u3000]*$/;

/**
 * @param {string} value
 * @returns {boolean}
 */
function isJavaBlank(value) {
    return JAVA_BLANK.test(value);
}

/**
 * Mirror of `PathUtils.extractSlugSource`: the slug, else the title, else the template name.
 * @param {string} slug
 * @param {string} title
 * @param {string} templateName
 * @returns {string}
 */
function slugSource(slug, title, templateName) {
    if (!isJavaBlank(slug)) return slug;
    if (!isJavaBlank(title)) return title;
    return templateName;
}

/**
 * Mirror of the three-argument `PathUtils.generatePagePath` without the extension and the
 * fragments directory. A slug with `/` becomes folders only when at least two segments
 * survive slugifying; otherwise the whole slug source is slugified as one name (its slashes
 * dropped), then the template name, then `page`.
 * @param {string} slug
 * @param {string} title
 * @param {string} templateName
 * @returns {string}
 */
function relativeSlugPath(slug, title, templateName) {
    if (!isJavaBlank(slug) && slug.includes('/')) {
        const parts = slug.split('/').map((segment) => slugify(segment, true)).filter(Boolean);
        if (parts.length >= 2) return parts.join('/');
    }
    return slugify(slugSource(slug, title, templateName), true) || slugify(templateName) || 'page';
}

/**
 * Predicts the URL the backend will produce for a page with the given slug/title, as an
 * exact mirror of `PathUtils.generatePagePath` with the slug prefix and render mode:
 * - a slug with `/` ignores the slug prefix;
 * - otherwise a slug gets the prefix in any mode, and a PAGE without a slug gets the
 *   prefix in front of its title (or template name);
 * - the result is slugified per segment, falling back to one slugified name when fewer than
 *   two segments survive (so a prefixed title that slugifies to nothing gives the prefix
 *   itself, and a title with `/` keeps its folders only with a prefix);
 * - INDEX_FRAGMENT pages live under `fragments/`.
 * The slug and title are trimmed first, as the form does before sending them. A homepage
 * template has a fixed path ({@link HOMEPAGE_PATH}): slug, title and slug prefix are ignored,
 * matching the backend's `PathUtils.pagePathFor`. `prefix` is every folder of the path (with
 * its trailing `/`) and `slug` its last segment.
 *
 * @param {{slug?: string, title?: string, templateName?: string, slugPrefix?: string|null, domain?: string|null, mode?: RenderMode, homepage?: boolean}} options
 * @returns {{domain: string|null, prefix: string, slug: string, path: string}}
 */
export function previewPagePath(options) {
    const {slug = '', title = '', templateName = '', slugPrefix = null, domain = null, mode = RenderMode.PAGE, homepage = false} = options || {};
    const cleanDomain = (domain || '').trim().replace(/\/$/, '') || null;

    if (homepage) {
        return {domain: cleanDomain, prefix: '', slug: '', path: HOMEPAGE_PATH};
    }

    const trimmedTitle = (title || '').trim();
    let source = (slug || '').trim();
    const safePrefix = slugify(slugPrefix || '', true);
    const multiSegment = !isJavaBlank(source) && source.includes('/');
    if (safePrefix && !multiSegment) {
        if (!isJavaBlank(source)) {
            source = `${safePrefix}/${source}`;
        } else if (mode === RenderMode.PAGE) {
            source = `${safePrefix}/${slugSource(source, trimmedTitle, templateName)}`;
        }
    }

    const relative = relativeSlugPath(source, trimmedTitle, templateName);
    const path = mode === RenderMode.INDEX_FRAGMENT ? `${FRAGMENT_PATH_PREFIX}${relative}` : relative;
    const lastSlash = path.lastIndexOf('/');

    return {
        domain: cleanDomain,
        prefix: path.slice(0, lastSlash + 1),
        slug: path.slice(lastSlash + 1),
        path
    };
}

/**
 * Mirror of `PathUtils.editedPagePathFor`: the address an edit gives a live page now at
 * `livePath`. It is {@link previewPagePath}, except that the page keeps `livePath` while the
 * slug rules from before accents were transliterated still give it; those rules dropped every
 * letter outside a-z ("Café" became `caf`), which equals today's rules on the text without
 * its non-ASCII characters. Without `livePath` (a new page or draft) it is
 * {@link previewPagePath}. Paths are compared as public URL paths.
 * @param {Parameters<typeof previewPagePath>[0]} options
 * @param {string|null} [livePath] the file path of the live page
 * @returns {ReturnType<typeof previewPagePath>}
 */
export function editedPagePath(options, livePath = null) {
    const preview = previewPagePath(options);
    if (!livePath || pageUrlPath(preview.path) === pageUrlPath(livePath)) return preview;
    const ascii = (value) => (typeof value === 'string' ? value.replace(/[^\x00-\x7F]/g, '') : value);
    const {slug, title, templateName, slugPrefix} = options || {};
    const legacy = previewPagePath({
        ...options,
        slug: ascii(slug),
        title: ascii(title),
        templateName: ascii(templateName),
        slugPrefix: ascii(slugPrefix),
    });
    return pageUrlPath(legacy.path) === pageUrlPath(livePath) ? legacy : preview;
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
function toBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

/**
 * Human-friendly label for a template name: drops a trailing "page" word,
 * turns dashes and underscores into spaces and uses sentence case while
 * keeping all-caps words (acronyms) intact.
 * @param {string} name
 * @returns {string}
 */
export function displayName(name) {
    const words = (name || '').replace(/[-_\s]+page$/i, '').split(/[-_\s]+/).filter(Boolean);
    return words.map((word, index) => {
        if (word.length > 1 && word === word.toUpperCase()) return word;
        const lower = word.toLowerCase();
        return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    }).join(' ');
}

/**
 * Public URL path of a page file, matching the canonical URL the backend
 * generates: leading slash, no ".html", and "/" for the index page.
 * @param {string} path e.g. "projects/villa.html"
 * @returns {string} e.g. "/projects/villa"
 */
export function pageUrlPath(path) {
    const trimmed = (path || '').replace(/^\/+/, '').replace(/\.html$/, '');
    return trimmed === '' || trimmed === 'index' ? '/' : `/${trimmed}`;
}

/**
 * Whether a page already exists on the live site. A published page does, and so does a
 * staged edit (its original stays live until the edit is published); a brand-new staged
 * draft does not.
 * @param {{published?: boolean, operation?: string|null}} page
 * @returns {boolean}
 */
export function hasLiveVersion(page) {
    return page.published !== false || page.operation === StagedOperation.EDIT;
}

/**
 * Whether a page is a staged edit of a page that is live: the original stays online and
 * the edit is not published yet.
 * @param {{published?: boolean, operation?: string|null}} page
 * @returns {boolean}
 */
export function hasPendingEdit(page) {
    return page.published === false && page.operation === StagedOperation.EDIT;
}

/**
 * Absolute live URL of a page on the client's domain, or null without a domain.
 * @param {string|null|undefined} domain e.g. "example.com" or "https://example.com/"
 * @param {string} path
 * @returns {string|null}
 */
export function liveUrl(domain, path) {
    const host = (domain || '').trim().replace(/\/+$/, '');
    if (!host) return null;
    const base = /^https?:\/\//.test(host) ? host : `https://${host}`;
    const urlPath = pageUrlPath(path);
    return urlPath === '/' ? `${base}/` : `${base}${urlPath}`;
}

/**
 * The pages whose title contains the search term, ignoring case and surrounding spaces. A
 * page without a title is matched on its path. A blank term keeps every page.
 * @template {{title?: string|null, path?: string}} P
 * @param {P[]} pages
 * @param {string} search
 * @returns {P[]}
 */
export function filterPagesByTitle(pages, search) {
    const term = (search || '').trim().toLocaleLowerCase();
    if (!term) return pages;
    return pages.filter((page) => String(page.title || page.path || '').toLocaleLowerCase().includes(term));
}
